import { Link, useNavigate, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Navbar } from "@/components/venture/Navbar";
import { SEOHead } from "@/components/venture/SEOHead";
import api from "@/lib/api";
import { toast } from "@/lib/toast";
import { scrollToSection } from "@/lib/scroll";
import { useAuth } from "@/context/AuthContext";
import { startRecommendationFlow, prefetchNextRoutes } from "@/lib/cta";
import { optimizeImg, srcSetFor } from "@/lib/imgUrl";
import WhyNotChatGPT from "@/components/venture/WhyNotChatGPT";
import {
  ArrowRight,
  Sparkles,
  Wallet,
  Star,
  GitCompare,
  Compass,
  Check,
  Crown,
  Award,
  Building,
  Building2,
  BadgeCheck,
} from "lucide-react";

/*
 * R109.26 — Below-fold sections (Testimonials, FAQ, Final CTA, Footer) live
 * in their own React.lazy chunk. We preload it on requestIdleCallback so the
 * chunk is already in cache by the time the user scrolls past pricing.
 * This shrinks the initial Landing chunk by ~30 KB gz and lets the Star icon,
 * Accordion primitives and Footer imports leave the critical path.
 */
const LandingBelowFold = lazy(() => import("@/pages/LandingBelowFold"));

// R214 — Mobile scroll restore. Clicking a hero journey CTA stashes the
// current scroll position in sessionStorage; on return (browser back) we
// re-apply it after the async category/pricing fetches have settled so the
// user lands back on the same section instead of the top of the page.
// Note: on this app body carries `overflow: hidden auto` and is the actual
// scroll container, so we read/write body.scrollTop (falling back to
// documentElement) rather than window.scrollY alone.
const LANDING_SCROLL_KEY = "vk_landing_scroll_v1";
const readScrollY = () => (
  window.scrollY
  || (document.scrollingElement && document.scrollingElement.scrollTop)
  || document.body.scrollTop
  || document.documentElement.scrollTop
  || 0
);
const writeScrollY = (y) => {
  try { window.scrollTo({ top: y, behavior: "auto" }); } catch (_) {}
  try { if (document.scrollingElement) document.scrollingElement.scrollTop = y; } catch (_) {}
  try { document.body.scrollTop = y; } catch (_) {}
  try { document.documentElement.scrollTop = y; } catch (_) {}
};
const stashLandingScroll = () => {
  try { sessionStorage.setItem(LANDING_SCROLL_KEY, JSON.stringify({ y: readScrollY(), t: Date.now() })); } catch (_) {}
};

export default function Landing() {
  const nav = useNavigate();
  const loc = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const goCta = () => {
    stashLandingScroll();
    startRecommendationFlow({ user, nav });
  };
  const [categories, setCategories] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.categories || []));
    api.get("/payments/pricing").then((r) => setPricing(r.data.tiers));
    api.get("/public/branding").then((r) => setBranding(r.data));
  }, []);

  /* R109.26 — Warm the LandingBelowFold chunk on idle so it is already in
     the module cache by the time the user scrolls past pricing. */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
    const id = ric(() => { import("@/pages/LandingBelowFold"); }, { timeout: 2500 });
    return () => (window.cancelIdleCallback || clearTimeout)(id);
  }, []);

  /* R214 — If we're returning to this page (back from /onboarding etc.) and
     we stashed a scrollY within the last 15 min, restore it after content
     settles. Honor a hash (#anchor) first — hash scroll owns the viewport. */
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (loc.hash) return;
    let raw;
    try { raw = sessionStorage.getItem(LANDING_SCROLL_KEY); } catch (_) { return; }
    if (!raw) return;
    try {
      const { y, t: ts } = JSON.parse(raw);
      if (!y || Date.now() - ts > 15 * 60 * 1000) {
        sessionStorage.removeItem(LANDING_SCROLL_KEY);
        return;
      }
      // Content (categories, pricing, below-fold chunk) paints async; retry
      // the restore until the body is tall enough for y to be valid.
      const times = [60, 220, 500, 900, 1400];
      const timers = times.map((ms) => setTimeout(() => {
        const bodyH = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
        if (bodyH >= y + window.innerHeight) {
          writeScrollY(y);
          sessionStorage.removeItem(LANDING_SCROLL_KEY);
        }
      }, ms));
      return () => timers.forEach(clearTimeout);
    } catch (_) { /* ignore */ }
  }, [loc.hash]);

  // Handle #anchor hash on mount + hash changes. Waits for content to paint
  // (Trust Stats, Trending, Sample-Report card etc. fetch async) so the target
  // section is settled at its final position before we scroll.
  useEffect(() => {
    if (!loc.hash) return;
    const anchor = loc.hash.replace("#", "");
    if (!anchor) return;
    // First attempt: scroll now (may be off if content still loading).
    // Then re-attempt at 300ms + 700ms to correct offset after async paints.
    const times = [0, 300, 700, 1400];
    const timers = times.map((ms) =>
      setTimeout(() => { scrollToSection(anchor); }, ms)
    );
    return () => timers.forEach(clearTimeout);
  }, [loc.hash]);

  // ---- Sample Ventures showcase ----
  const industryFilters = [
    { slug: "all",           label: "All" },
    { slug: "retail",        label: "Retail" },
    { slug: "manufacturing", label: "Manufacturing" },
    { slug: "food-beverage", label: "Food" },
    { slug: "finance",       label: "Finance" },
    { slug: "healthcare",    label: "Healthcare" },
    { slug: "education",     label: "Education" },
    { slug: "digital",       label: "Technology" },
    { slug: "agriculture",   label: "Agriculture" },
    { slug: "fitness",       label: "Fitness" },
    { slug: "real-estate",   label: "Real Estate" },
    { slug: "services",      label: "Services" },
    { slug: "hospitality",   label: "Hospitality" },
    { slug: "auto",          label: "Auto" },
  ];

  // Curated slug list for a diverse first-impression showcase.
  const SHOWCASE_SLUGS = [
    "medical-store", "pharmacy", "diagnostic-lab", "boutique-gym",
    "preschool", "cloud-kitchen", "dairy-farm", "solar-installation-business",
    "car-service-workshop", "mobile-accessories-store", "mutual-fund-distribution",
    "property-brokerage", "furniture-manufacturing", "homestay-business",
    "jewellery-store", "digital-marketing-agency",
  ];
  const [industryFilter, setIndustryFilter] = useState("all");

  const showcaseVentures = (() => {
    if (!categories.length) return [];
    // Try to hit the curated slug list first
    const bySlug = new Map(categories.map((c) => [c.slug, c]));
    const curated = SHOWCASE_SLUGS.map((s) => bySlug.get(s)).filter(Boolean);
    let pool = curated.length >= 8 ? curated : categories.slice();
    // Fill from other industries if curated is short
    if (curated.length < 16) {
      const seen = new Set(curated.map((c) => c.slug));
      const industryFirstPick = new Map();
      for (const c of categories) {
        if (seen.has(c.slug)) continue;
        const ind = c.industry || "misc";
        if (!industryFirstPick.has(ind)) industryFirstPick.set(ind, c);
      }
      pool = [...curated, ...industryFirstPick.values()];
    }
    // Apply industry filter
    if (industryFilter !== "all") {
      pool = categories.filter((c) => (c.industry || "").toLowerCase() === industryFilter);
      // Diversify by venture_type inside a single industry
      const seenType = new Set();
      pool = pool.filter((c) => {
        const t = c.venture_type || c.slug;
        if (seenType.has(t)) return false;
        seenType.add(t);
        return true;
      });
    }
    return pool.slice(0, 8);
  })();

  const uniqueIndustryCount = new Set(categories.map((c) => c.industry).filter(Boolean)).size || 17;
  const uniqueVentureTypeCount = new Set(categories.map((c) => c.venture_type).filter(Boolean)).size || 58;
  const totalVentureCount = categories.length;

  return (
    <div>
      <Navbar />
      <SEOHead
        title="VentureKhoj™ — From Discovery to Decision | AI feasibility reports for India"
        description="From Discovery to Decision — discover the best business, franchise or budget opportunity based on your budget, city and skills. Get an tailor-made feasibility report combining structured business intelligence and market research with financials, SWOT, ROI and 90-day roadmap."
        path="/"
      />

      {/* HERO — problem-first, solution second */}
      <section className="grid-noise text-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-10 sm:pt-14 pb-16 lg:pt-20 lg:pb-24 lg:grid lg:grid-cols-12 lg:items-center lg:gap-10">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-200">
              <Sparkles className="h-3.5 w-3.5" />
              {t("landing.hero_pill")}
            </div>
            <h1
              data-testid="landing-hero-title"
              className="font-heading mt-5 text-[28px] font-black leading-[1.15] tracking-tight sm:text-4xl lg:text-[52px] lg:leading-[1.08]"
            >
              {t("landing.hero_title_line1", "Find the Right Business to")}{" "}
              <span className="text-emerald-400">{t("landing.hero_title_line2", "Start in Your City")}</span>
            </h1>

            {/* R110.68 — Sharper single-sentence subheadline (removes the
                pain-points list + the long value-prop paragraph so the
                dual-path callouts land faster). */}
            <p className="mt-5 max-w-xl text-[15px] sm:text-base text-white/90 leading-relaxed" data-testid="landing-hero-intro">
              {t("landing.hero_intro", "Discover business ideas that fit your budget, city, skills and interests. Compare options, calculate costs and make a more informed decision.")}
            </p>
            <p className="mt-4 max-w-xl text-[15px] sm:text-base text-white/90 leading-relaxed" data-testid="landing-hero-lead">
              {t("landing.hero_sub_lead", "Choosing the right business isn't easy — pick your ideal path:")}
            </p>

            {/* Dual-path callouts — kept, cleaner rendering */}
            <ul className="mt-5 space-y-2 text-[15px] text-white/95" data-testid="landing-hero-dual-callouts">
              <li className="flex items-start gap-2.5">
                <span className="mt-0.5 text-lg leading-none">📄</span>
                <span>
                  <b className="text-white">{t("landing.hero_dual_brand_label", "Build Your Own Brand:")}</b>{" "}
                  {t("landing.hero_dual_brand_body", "We give you complete cost plans, profit calculations, and step-by-step guides to launch independently.")}
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-0.5 text-lg leading-none">🚀</span>
                <span>
                  <b className="text-white">{t("landing.hero_dual_franchise_label", "Partner with a Franchise:")}</b>{" "}
                  {t("landing.hero_dual_franchise_body", "Pick from pre-checked, successful brands with ready systems and proven returns.")}
                </span>
              </li>
            </ul>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button
                data-testid="landing-cta-primary"
                onClick={goCta}
                onMouseEnter={prefetchNextRoutes}
                onFocus={prefetchNextRoutes}
                className="rounded-full bg-emerald-500 px-6 py-6 text-base font-semibold text-white hover:bg-emerald-600 btn-hover-lift"
              >
                {t("landing.hero_cta_primary", "Find My Best-Fit Business")}
                <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
              <Button
                data-testid="landing-cta-secondary"
                onClick={() => nav("/business-ideas")}
                onMouseEnter={prefetchNextRoutes}
                onFocus={prefetchNextRoutes}
                variant="outline"
                className="rounded-full px-6 py-6 text-base font-semibold border-white/25 bg-white/5 text-white hover:bg-white/10 hover:text-white"
              >
                {t("landing.hero_cta_secondary", "Browse Business Ideas")}
              </Button>
              <Button
                data-testid="landing-cta-verified-franchises"
                onClick={() => nav("/business-partners")}
                onMouseEnter={prefetchNextRoutes}
                onFocus={prefetchNextRoutes}
                className="rounded-full px-6 py-6 text-base font-semibold bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                <BadgeCheck className="mr-2 h-4 w-4" />
                {t("landing.cta_verified_franchises", "Explore Business Partners")}
              </Button>
            </div>


            {/* Trust strip — compact credibility signals */}
            <div
              className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm sm:text-base uppercase tracking-widest text-slate-400"
              data-testid="landing-hero-trust-strip"
            >
              <span className="inline-flex items-center gap-1.5" data-testid="landing-hero-trust-1">
                <span className="text-emerald-400 font-bold">100+</span>
                <span className="normal-case tracking-normal text-slate-300">{t("landing.hero_trust_ideas", "business ideas")}</span>
              </span>
              <span className="h-1 w-1 rounded-full bg-slate-600" aria-hidden />
              <span className="inline-flex items-center gap-1.5" data-testid="landing-hero-trust-2">
                <span className="text-emerald-400 font-bold">500+</span>
                <span className="normal-case tracking-normal text-slate-300">{t("landing.hero_trust_cities", "cities")}</span>
              </span>
              <span className="h-1 w-1 rounded-full bg-slate-600 hidden sm:inline-block" aria-hidden />
              <span className="inline-flex items-center gap-1.5" data-testid="landing-hero-trust-3">
                <span className="text-emerald-400 font-bold">17</span>
                <span className="normal-case tracking-normal text-slate-300">{t("landing.hero_trust_industries", "industries")}</span>
              </span>
            </div>
          </div>

          {/* Instant Match Preview — replaces the old Feasibility Report card */}
          <div className="mt-14 lg:mt-0 lg:col-span-6">
            <div className="relative">
              <div className="absolute -top-3 -left-3 h-24 w-24 rounded-2xl bg-emerald-500/30 blur-2xl" aria-hidden />
              <div className="absolute -bottom-4 -right-4 h-32 w-32 rounded-full bg-amber-500/30 blur-3xl" aria-hidden />
              <JourneyFlow ideas={categories} count={totalVentureCount} />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — R92.1 4-card walkthrough restored (JourneyStory
          removed in favour of a compact single-row summary). */}
      <section
        id="how-it-works"
        className="mx-auto max-w-7xl px-5 sm:px-8 pt-16 pb-10 sm:pb-12 cv-auto"
        data-testid="landing-how-it-works"
      >
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-700">
            {t("nav.how_it_works", "How it works")}
          </div>
          <h2 className="font-heading mt-3 text-3xl font-bold text-slate-900 sm:text-4xl">
            {t("landing_more.hiw_headline", "From Discovery to Decision")}
          </h2>
        </div>

        <div className="mt-12 relative">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 md:gap-4 lg:gap-5 items-stretch">
            {[
              {
                step: "01",
                icon: Building,
                title: t("landing_more.hiw_step1_title", "Create Your Business Profile"),
                body: t(
                  "landing_more.hiw_step1_body",
                  "Tell us about your budget, location, goals, and working style to unlock personalized business matches.",
                ),
                tone: "emerald",
              },
              {
                step: "02",
                icon: Compass,
                title: t("landing_more.hiw_step2_title", "Discover Your Best Matches"),
                body: t(
                  "landing_more.hiw_step2_body",
                  "Our engine analyzes your profile and surfaces top business ideas fitted for your budget and city tier.",
                ),
                tone: "indigo",
              },
              {
                step: "03",
                icon: GitCompare,
                title: t("landing_more.hiw_step3_title", "Choose Your Path (DIY or Franchise)"),
                body: t(
                  "landing_more.hiw_step3_body",
                  "Explore business setup requirements on your own OR view up to 3 curated, verified franchise brands in that domain.",
                ),
                tone: "amber",
              },
              {
                step: "04",
                icon: Award,
                title: t("landing_more.hiw_step4_title", "Compare, Decide & Connect"),
                body: t(
                  "landing_more.hiw_step4_body",
                  "Generate in-depth Feasibility Reports to build from scratch, or explicitly select up to 5 franchise brands total to request direct callbacks.",
                ),
                tone: "purple",
              },
            ].map(({ step, icon: Icon, title, body, tone }, idx) => {
              const toneMap = {
                emerald: { chip: "bg-emerald-50 text-emerald-700", ring: "ring-emerald-100" },
                indigo:  { chip: "bg-indigo-50 text-indigo-700",   ring: "ring-indigo-100" },
                amber:   { chip: "bg-amber-50 text-amber-700",     ring: "ring-amber-100" },
                purple:  { chip: "bg-purple-50 text-purple-700",   ring: "ring-purple-100" },
              }[tone];
              return (
                <div key={step} className="relative group" data-testid={`hiw-card-${step}`}>
                  <Card
                    className={`h-full card-lift rounded-2xl border-slate-200 bg-white ring-1 ${toneMap.ring} transition-all duration-300 hover:-translate-y-1 hover:shadow-lg`}
                  >
                    <CardContent className="p-6 sm:p-7 flex flex-col h-full">
                      <div className="flex items-center gap-3">
                        <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${toneMap.chip}`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <span className="font-heading text-sm font-bold text-slate-400 tracking-widest">
                          STEP {step}
                        </span>
                      </div>
                      <h3 className="mt-5 font-heading text-lg font-bold text-slate-900 leading-snug">
                        {title}
                      </h3>
                      <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">
                        {body}
                      </p>
                    </CardContent>
                  </Card>

                  {idx < 3 && (
                    <div
                      className="hidden lg:flex absolute top-1/2 -right-3 -translate-y-1/2 z-10 h-6 w-6 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm ring-1 ring-slate-200"
                      aria-hidden="true"
                    >
                      <ArrowRight className="h-3.5 w-3.5" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* R110.160 — "What You Get" (reports-overview) section removed
          platform-wide per owner request. */}

      {/* R141 — "Why not just ask ChatGPT?" comparison table. */}
      <WhyNotChatGPT onCta={goCta} />

      {/* R110.65 — Pricing section rewritten after the B2C paywall pivot.
          Users: everything is free. Franchise brands: pay only per verified
          buyer, tiered by minimum investment. */}
      <section id="pricing" className="bg-white border-t border-slate-200 cv-auto" data-testid="landing-pricing-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-700">{t("landing_pricing.eyebrow", "Pricing")}</div>
            <h2 className="font-heading mt-3 text-2xl font-bold text-slate-900 sm:text-3xl lg:text-4xl">
              {t("landing_pricing.headline_wallet", "Pay only for what you use.")}
            </h2>
            <p className="mt-2 text-slate-600">
              {t("landing_pricing.sub_wallet", "No subscription. Top up a wallet once (from ₹49) and spend it across the platform. Bigger top-ups get bonus credit.")}
            </p>
          </div>

          {/* R217 — Wallet / pay-per-use model (replaced the old 30-day plans). */}
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 items-stretch" data-testid="landing-pricing-tiers">
            {[
              { id: "custom",      name: t("landing_pricing.w_custom", "Custom numbers"),     free: t("landing_pricing.w_customfree", "Login only"), price: t("landing_pricing.free_price", "Free"), isFree: true },
              { id: "match",       name: t("landing_pricing.w_match", "Personalised match"),  free: t("landing_pricing.w_matchfree", "Login only"), price: t("landing_pricing.free_price", "Free"), isFree: true },
              { id: "compare",     name: t("landing_pricing.w_compare", "Comparison"),        free: t("landing_pricing.w_3free", "Pay per use"), price: "₹49" },
              { id: "feasibility", name: t("landing_pricing.w_report", "Feasibility report"), free: t("landing_pricing.w_1free", "Pay per use"), price: "₹99" },
            ].map((tier) => (
              <Card key={tier.id} data-testid={`pricing-card-${tier.id}`} className="rounded-3xl flex flex-col border border-slate-200 bg-white">
                <CardContent className="p-6 flex flex-col flex-1">
                  <h3 className="font-heading text-lg font-bold text-slate-900">{tier.name}</h3>
                  <div className="mt-3 inline-flex items-center gap-1.5 self-start rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 text-xs font-bold">
                    <Sparkles className="h-3.5 w-3.5" /> {tier.free}
                  </div>
                  <div className="mt-3 text-3xl font-heading font-black text-slate-900">
                    {tier.price}{!tier.isFree && <span className="text-sm font-semibold text-slate-500"> {t("landing_pricing.then_each", "then / each")}</span>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 flex flex-col items-center gap-3">
            <Button
              data-testid="landing-pricing-wallet-cta"
              onClick={() => nav("/pricing")}
              className="rounded-full h-12 px-8 font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Wallet className="h-5 w-5 mr-2" /> {t("landing_pricing.wallet_cta", "See wallet & add money")}
            </Button>
            <div className="text-center text-xs text-slate-500">
              <Link to="/brands/pricing" className="underline text-emerald-700">{t("landing_pricing.brands_link", "Brands & franchisors →")}</Link>
            </div>
          </div>
        </div>
      </section>


      {/* CATEGORIES — showcase section */}
      <section id="explore-businesses" className="bg-white border-y border-slate-200 cv-auto" data-testid="landing-explore">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
          {/* Header — Option 1: inspirational, one-liner, left-aligned */}
          <h2
            data-testid="showcase-h2"
            className="text-left font-heading text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 leading-tight tracking-tight"
          >
            {t("landing_more.showcase_headline_prefix", "Discover Business")} <span className="text-emerald-600">{t("landing_more.showcase_headline_accent", "Ideas")}</span>
          </h2>
          <p className="mt-3 max-w-3xl text-sm sm:text-base text-slate-600 leading-relaxed" data-testid="showcase-subtitle">
            {t("landing_more.showcase_sub_long", "Browse curated business opportunities across retail, manufacturing, food, finance, healthcare, technology, education, real estate and more.")}
          </p>

          {/* Filter chips + Primary CTA */}
          <div className="mt-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div
              className="-mx-5 sm:-mx-8 lg:mx-0 flex gap-2 overflow-x-auto px-5 sm:px-8 lg:px-0 pb-2 lg:pb-0 lg:flex-wrap scrollbar-hide"
              data-testid="showcase-filter-chips"
            >
              {industryFilters.map((f) => {
                const active = industryFilter === f.slug;
                return (
                  <button
                    key={f.slug}
                    data-testid={`filter-chip-${f.slug}`}
                    onClick={() => setIndustryFilter(f.slug)}
                    className={`shrink-0 rounded-full px-4 h-9 text-sm font-medium transition ${
                      active
                        ? "bg-emerald-600 text-white shadow-sm"
                        : "bg-slate-100 text-slate-700 hover:bg-emerald-100 hover:text-emerald-800"
                    }`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
            <Button
              data-testid="showcase-cta-primary"
              onClick={() => nav("/business-ideas")}
              onMouseEnter={prefetchNextRoutes}
              onFocus={prefetchNextRoutes}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-6 whitespace-nowrap shrink-0"
            >
              Explore All {totalVentureCount || "100"}+ Businesses
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {/* Venture cards */}
          <div
            className="mt-8 -mx-5 sm:-mx-8 lg:mx-0 flex gap-4 overflow-x-auto snap-x snap-mandatory px-5 sm:px-8 lg:px-0 lg:grid lg:grid-cols-4 pb-4 lg:pb-0 scrollbar-hide"
            data-testid="showcase-venture-grid"
          >
            {showcaseVentures.length === 0 && [...Array(8)].map((_, i) => (
              <div key={i} className="min-w-[260px] lg:min-w-0 h-72 rounded-2xl bg-slate-100 animate-pulse" />
            ))}
            {showcaseVentures.map((c) => (
              <Card
                key={c.id}
                data-testid={`showcase-venture-${c.slug}`}
                onClick={() => nav(`/venture/${c.slug}`)}
                className="card-lift snap-start shrink-0 w-[260px] sm:w-[280px] lg:w-auto rounded-2xl overflow-hidden border-slate-200 bg-white cursor-pointer flex flex-col hover:border-emerald-300 hover:shadow-lg transition"
              >
                <div className="h-32 w-full overflow-hidden bg-gradient-to-br from-emerald-100 to-slate-100 relative">
                  <img
                    src={optimizeImg(c.image_url, 480)}
                    srcSet={srcSetFor(c.image_url, [280, 400, 560])}
                    sizes="(max-width: 640px) 260px, (max-width: 1024px) 280px, 300px"
                    alt={c.name}
                    width="600"
                    height="200"
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  {c.industry && (
                    <div className="absolute top-2 left-2 rounded-full bg-white/95 backdrop-blur px-2.5 py-0.5 text-[11px] font-medium text-emerald-800 capitalize shadow-sm">
                      {c.industry.replace(/-/g, " ")}
                    </div>
                  )}
                </div>
                <CardContent className="p-5 flex flex-col flex-1">
                  <div className="font-heading text-base font-bold text-slate-900 line-clamp-1">{c.name}</div>
                  <div className="mt-1 text-xs text-slate-500 line-clamp-2 min-h-[2rem]">{c.tagline}</div>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-700">
                    <Wallet className="h-3.5 w-3.5 text-emerald-600" />
                    <span className="font-medium">
                      ₹{(c.min_capital / 100000).toFixed(0)}L – ₹{(c.max_capital / 100000).toFixed(0)}L
                    </span>
                  </div>
                  <div
                    className="mt-auto pt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 group-hover:text-emerald-800 group-hover:gap-2 transition-all"
                    data-testid={`showcase-venture-cta-${c.slug}`}
                  >
                    View Details
                    <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Mid-section conversion banner */}
          <div
            data-testid="showcase-mid-banner"
            className="mt-12 rounded-3xl text-white p-8 sm:p-10 relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, #0a1f44 0%, #0f172a 55%, #065f46 100%)" }}
          >
            <div className="grid gap-6 lg:grid-cols-[1fr_auto] items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-medium">
                  <Sparkles className="h-3.5 w-3.5 text-emerald-300" /> AI-powered matching
                </div>
                <h3 className="mt-4 font-heading text-2xl sm:text-3xl font-black leading-tight">
                  Not sure which business is right for you?
                </h3>
                <p className="mt-3 text-slate-200 leading-relaxed max-w-2xl">
                  Our AI analyses your budget, interests, skills, experience and city to match
                  the businesses that best fit your profile.
                </p>
              </div>
              <Button
                data-testid="showcase-mid-cta"
                onClick={goCta}
                onMouseEnter={prefetchNextRoutes}
                onFocus={prefetchNextRoutes}
                className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 h-12 px-6 font-bold whitespace-nowrap"
              >
                Find My Best-Fit Business
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Below-fold: Testimonials + FAQ + Final CTA + Footer.
          Lazy-loaded (R109.26). Preloaded on requestIdleCallback in the
          effect below so the chunk is warm by the time the user scrolls.
          Placeholder min-height (~2300px on mobile, ~1600px on desktop)
          reserves enough space to prevent bottom-of-page CLS when the
          chunk hydrates. */}
      <Suspense
        fallback={
          <div
            aria-hidden
            className="cv-auto"
            style={{ minHeight: "clamp(1600px, 240vh, 2400px)" }}
          />
        }
      >
        <LandingBelowFold />
      </Suspense>
    </div>
  );
}


// ---------------------------------------------------------------------------
// JourneyFlow — hero visual: the real 4-step platform flow (replaces the old
// budget slider). Uses live ideas + the current navy / emerald / amber theme.
// ---------------------------------------------------------------------------
function IdeaThumb({ idea, selected = false }) {
  return (
    <div className={`relative h-9 w-9 shrink-0 overflow-hidden rounded-lg border bg-slate-100 ${selected ? "border-emerald-500 ring-2 ring-emerald-500/30" : "border-slate-200"}`}>
      {idea?.image_url ? (
        <img src={optimizeImg(idea.image_url, 120)} alt={idea?.name || ""} loading="lazy" className="h-full w-full object-cover" />
      ) : (
        <div className="h-full w-full bg-gradient-to-br from-slate-200 to-slate-300" />
      )}
      {selected && (
        <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white ring-2 ring-white">
          <Check className="h-2.5 w-2.5" />
        </span>
      )}
    </div>
  );
}

const JOURNEY_ACCENTS = {
  1: "bg-navy text-white",
  2: "bg-emerald-600 text-white",
  3: "bg-amber-500 text-white",
  4: "bg-emerald-700 text-white",
};

function JourneyStep({ n, title, desc, children, last, onClick, cta }) {
  return (
    <div className="relative flex gap-3" data-testid={`journey-step-${n}`}>
      <div className="flex flex-col items-center">
        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${JOURNEY_ACCENTS[n]}`}>{n}</span>
        {!last && <span className="mt-1 w-px flex-1 bg-slate-200" aria-hidden />}
      </div>
      <button
        type="button"
        onClick={onClick}
        data-testid={`journey-step-link-${n}`}
        className="group grid min-w-0 flex-1 grid-cols-1 items-center gap-x-3 gap-y-2 pb-4 text-left outline-none sm:grid-cols-[auto_minmax(0,1fr)]"
      >
        <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-2.5 transition-colors group-hover:border-emerald-300 group-hover:bg-emerald-50/50 group-focus-visible:border-emerald-400">
          {children}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1 font-heading text-sm font-bold text-slate-900">
            <span className="transition-colors group-hover:text-emerald-700">{title}</span>
            <ArrowRight className="h-3.5 w-3.5 -translate-x-1 shrink-0 text-emerald-600 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
          </div>
          <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{desc}</p>
          {cta && (
            <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 underline-offset-2 group-hover:underline">
              {cta} <ArrowRight className="h-3 w-3" />
            </span>
          )}
        </div>
      </button>
    </div>
  );
}

function JourneyFlow({ ideas = [], count = 0 }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user } = useAuth();
  const pics = ideas.filter((c) => c && c.image_url);
  const shown = count || 100;
  const [rot, setRot] = useState(0);

  // Live rotation — refresh the real idea thumbnails in steps 2 & 3 every 3s so
  // the hero feels alive. Respects reduced-motion; only runs with a surplus.
  useEffect(() => {
    if (pics.length <= 5) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => setRot((r) => r + 1), 3000);
    return () => clearInterval(id);
  }, [pics.length]);

  const windowOf = (start, n) =>
    pics.length ? Array.from({ length: Math.min(n, pics.length) }, (_, i) => pics[(start + i) % pics.length]) : [];
  const match5 = windowOf(rot, 5);
  const compare3 = windowOf(rot + 2, 3);

  const goMatch = () => { stashLandingScroll(); startRecommendationFlow({ user, nav }); };
  const goBrowse = () => { stashLandingScroll(); nav("/business-ideas"); };
  const goSample = () => { stashLandingScroll(); nav("/sample-report/view"); };

  return (
    <div className="rounded-3xl bg-white p-5 shadow-2xl ring-1 ring-slate-900/5 sm:p-6" data-testid="hero-journey-flow">
      <div className="inline-flex items-center gap-1.5 text-emerald-700">
        <Sparkles className="h-4 w-4" />
        <span className="text-[11px] font-bold uppercase tracking-[0.16em]">{t("landing.hero_journey_eyebrow", "From Discovery to Decision")}</span>
      </div>
      <h3 className="mt-2 font-heading text-lg font-black leading-tight text-slate-900 sm:text-xl">
        {t("landing.hero_journey_title", "Your journey from {{count}}+ ideas to a detailed plan", { count: shown })}
      </h3>
      <p className="mt-1 text-xs text-slate-500 sm:text-sm">
        {t("landing.hero_journey_sub", "A simple, step-by-step way to find and evaluate the right business for you.")}
      </p>

      <div className="mt-5">
        <JourneyStep n={1} onClick={goMatch}
          title={t("landing.hero_journey_s1_title", "Tell us about yourself")}
          desc={t("landing.hero_journey_s1_desc", "Answer 8 quick questions about your budget, city, skills and interests.")}>
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-bold text-slate-700">{t("landing.hero_journey_s1_card", "8 quick questions")}</div>
            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">{shown}+ {t("landing.hero_journey_s1_chip", "ideas")}</span>
          </div>
          <div className="mt-2 space-y-1.5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-500 text-white"><Check className="h-2.5 w-2.5" /></span>
                <span className="h-1.5 rounded-full bg-slate-200" style={{ width: `${72 - i * 16}%` }} />
              </div>
            ))}
          </div>
        </JourneyStep>

        <JourneyStep n={2} onClick={goMatch}
          title={t("landing.hero_journey_s2_title", "Get 5 personalised matches")}
          desc={t("landing.hero_journey_s2_desc", "We analyse your profile and recommend 5 business ideas that best fit you.")}>
          <div className="mb-2 text-[11px] font-bold text-slate-700">{t("landing.hero_journey_s2_card", "Your 5 best-fit ideas")}</div>
          <div key={rot} className="flex items-center gap-1.5 animate-in fade-in duration-500">
            {match5.map((c, i) => <IdeaThumb key={`${c.slug}-${i}`} idea={c} />)}
          </div>
        </JourneyStep>

        <JourneyStep n={3} onClick={goBrowse}
          title={t("landing.hero_journey_s3_title", "Compare and choose")}
          desc={t("landing.hero_journey_s3_desc", "Compare investment, expenses, demand and growth — then pick the one you like best.")}>
          <div className="mb-2 text-[11px] font-bold text-slate-700">{t("landing.hero_journey_s3_card", "Compare your options")}</div>
          <div key={rot} className="flex items-center justify-center gap-1.5 animate-in fade-in duration-500">
            {compare3.map((c, i) => (
              <div key={`${c.slug}-${i}`} className="flex items-center gap-1.5">
                <IdeaThumb idea={c} selected={i === 1} />
                {i < compare3.length - 1 && <span className="text-[9px] font-bold text-slate-400">VS</span>}
              </div>
            ))}
          </div>
        </JourneyStep>

        <JourneyStep n={4} last onClick={goSample}
          cta={t("landing.hero_journey_sample_link", "See a real feasibility report")}
          title={t("landing.hero_journey_s4_title", "Get detailed numbers & feasibility report")}
          desc={t("landing.hero_journey_s4_desc", "See estimated investment, monthly expenses, profit, break-even and a step-by-step roadmap.")}>
          <div className="mb-2 text-[11px] font-bold text-slate-700">{t("landing.hero_journey_s4_card", "Detailed numbers & feasibility report")}</div>
          <div className="flex items-end gap-3">
            <div className="flex h-10 items-end gap-1">
              {[40, 60, 52, 78, 64, 90].map((h, i) => (
                <span key={i} className="w-1.5 rounded-sm bg-emerald-500/80" style={{ height: `${h}%` }} />
              ))}
            </div>
            <div className="h-10 w-10 rounded-full" style={{ background: "conic-gradient(#059669 0 55%, #f59e0b 55% 80%, #1e293b 80% 100%)" }} aria-hidden />
            <div className="flex-1 space-y-1">
              <span className="block h-1.5 w-full rounded-full bg-slate-200" />
              <span className="block h-1.5 w-4/5 rounded-full bg-slate-200" />
              <span className="block h-1.5 w-3/5 rounded-full bg-slate-200" />
            </div>
          </div>
        </JourneyStep>
      </div>

      <Button
        data-testid="hero-journey-cta"
        onClick={goMatch}
        className="mt-1 w-full rounded-full bg-emerald-500 py-5 text-sm font-semibold text-white hover:bg-emerald-600"
      >
        {t("landing.hero_journey_cta", "Find My 5 Best-Fit Ideas")}
        <ArrowRight className="ml-1 h-4 w-4" />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CapitalSlider — hero-mounted budget matcher
// ---------------------------------------------------------------------------
const CAPITAL_STEPS = [
  100000, 300000, 500000, 800000, 1000000, 1500000, 2000000, 3000000, 5000000,
  7500000, 10000000, 15000000, 20000000, 30000000, 50000000, 75000000, 100000000,
];

// R110.1 — Persona cycle for the hero widget. Each persona pins a starting
// budget step + city label + one-line context. The widget auto-cycles every
// 5.5s until the visitor interacts (click a persona chip, drag the slider,
// click a match). This helps different visitor archetypes see themselves.
const HERO_PERSONAS = [
  { id: "student",     label: "Student",     capitalIdx: 2,  city: "Any Tier-2 city", context: "Low capital · Side income first" },
  { id: "salaried",    label: "Salaried",    capitalIdx: 5,  city: "Tier-1 metro",    context: "Weekend-safe · Passive-friendly" },
  { id: "family-run",  label: "Family-run",  capitalIdx: 8,  city: "Tier-2 / Tier-3", context: "Full-time · Team-scaled" },
];

function fmtRs(v) {
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(v % 10000000 === 0 ? 0 : 1)}Cr`;
  return `₹${Math.round(v / 100000)}L`;
}

function CapitalSlider({ variant = "inline" }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const isHero = variant === "hero";
  const [personaIdx, setPersonaIdx] = useState(1); // start at "Salaried"
  const [idx, setIdx] = useState(HERO_PERSONAS[1].capitalIdx);
  const [fit, setFit] = useState(null);
  const [autoCycle, setAutoCycle] = useState(false);  // R110.75 — persona stays on Salaried unless user taps another chip
  const capital = CAPITAL_STEPS[idx];
  const persona = HERO_PERSONAS[personaIdx];

  // Fetch fit whenever capital changes (debounced).
  useEffect(() => {
    const t = setTimeout(() => {
      api.get(`/categories/budget-fit?capital=${capital}`)
        .then((r) => setFit(r.data))
        .catch(() => setFit(null));
    }, 250);
    return () => clearTimeout(t);
  }, [capital]);

  // Auto-cycle personas until user interacts.
  // R110.52 — Disable auto-cycle on small screens. On mobile the card's
  // height changes with each persona switch (different match counts wrap
  // chips onto different row counts), which made the entire home page
  // "jump" every 5.5s. Users can still tap a persona chip to switch.
  useEffect(() => {
    if (!isHero || !autoCycle) return;
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches) return;
    const id = setInterval(() => {
      setPersonaIdx((p) => {
        const next = (p + 1) % HERO_PERSONAS.length;
        setIdx(HERO_PERSONAS[next].capitalIdx);
        return next;
      });
    }, 5500);
    return () => clearInterval(id);
  }, [isHero, autoCycle]);

  const pickPersona = (i) => {
    setAutoCycle(false);
    setPersonaIdx(i);
    setIdx(HERO_PERSONAS[i].capitalIdx);
  };
  const onSlide = (v) => { setAutoCycle(false); setIdx(v[0]); };

  return (
    <div
      data-testid={isHero ? "hero-match-preview" : "hero-capital-slider"}
      className={
        isHero
          ? "rounded-3xl border border-slate-200 bg-white text-slate-900 shadow-2xl shadow-black/25 p-6 sm:p-8"
          : "mt-10 rounded-2xl border border-white/10 bg-white/[0.06] backdrop-blur-sm p-5 sm:p-6"
      }
    >
      {isHero && (
        <>
          {/* Persona chip row — click to switch, auto-cycles until interaction */}
          <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="hero-persona-chips">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mr-1">Sample profile</span>
            {HERO_PERSONAS.map((p, i) => (
              <button
                key={p.id}
                onClick={() => pickPersona(i)}
                data-testid={`hero-persona-${p.id}`}
                aria-pressed={personaIdx === i}
                className={
                  "rounded-full px-2.5 py-0.5 text-[11px] font-semibold transition " +
                  (personaIdx === i
                    ? "bg-emerald-600 text-white shadow-sm"
                    : "bg-slate-100 border border-slate-200 text-slate-700 hover:bg-emerald-50 hover:border-emerald-300")
                }
              >
                {p.label}
              </button>
            ))}
          </div>
          {/* Persona detail row */}
          <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="hero-persona-detail">
            <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-800">
              {fmtRs(capital)} Budget
            </span>
            <span className="rounded-full bg-slate-100 border border-slate-200 px-2.5 py-0.5 text-[11px] font-semibold text-slate-700">
              {persona.city}
            </span>
            <span className="text-[11px] text-slate-500 hidden sm:inline" data-testid="hero-persona-context">
              · {persona.context}
            </span>
          </div>
        </>
      )}

      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className={`text-xs uppercase tracking-widest ${isHero ? "text-emerald-700 inline-flex items-center gap-1.5" : "text-emerald-300"}`}>
            {isHero && <Sparkles className="h-3.5 w-3.5" />}
            {isHero ? "Top Business Matches" : "Instant Match Preview"}
          </div>
          <div className={`mt-1 text-sm ${isHero ? "text-slate-600 text-base" : "text-slate-200"}`}>
            Your budget · <span className={`font-heading font-black ${isHero ? "text-slate-900 text-2xl sm:text-3xl" : "text-white text-lg"}`}>{fmtRs(capital)}</span>
          </div>
        </div>
        {fit && fit.total_fit > 0 && (
          <div className="text-right shrink-0 min-h-[64px]" data-testid="hero-fit-count-block">
            <div
              data-testid="hero-slider-count"
              className={`font-heading font-black leading-none ${isHero ? "text-emerald-600 text-5xl sm:text-6xl" : "text-emerald-300 text-3xl sm:text-4xl"}`}
            >
              {fit.total_fit}
            </div>
            <div className={`text-[11px] uppercase tracking-widest mt-1 ${isHero ? "text-slate-500" : "text-slate-400"}`}>business ideas</div>
          </div>
        )}
      </div>

      <div className={isHero ? "mt-7" : "mt-5"}>
        <Slider
          data-testid="hero-slider-input"
          value={[idx]}
          min={0}
          max={CAPITAL_STEPS.length - 1}
          step={1}
          onValueChange={onSlide}
        />
        <div className={`mt-2 flex justify-between text-[10px] font-mono ${isHero ? "text-slate-500" : "text-slate-400"}`}>
          <span>₹1L</span>
          <span>₹15L</span>
          <span>₹1Cr</span>
          <span>₹10Cr</span>
        </div>
      </div>

      {/* R110.52 — reserve a floor height so switching persona (which
          changes chip count) doesn't visibly shift the card / page. Chips
          render on 1–2 rows depending on persona; ~86px covers the taller
          case at every breakpoint. */}
      <div className="mt-4 min-h-[86px]">
        {fit && fit.sample_matches?.length > 0 && (
          <div className={`flex flex-wrap gap-1.5 ${isHero ? "gap-2" : ""}`} data-testid="hero-slider-chips">
            {fit.sample_matches.slice(0, isHero ? 6 : 4).map((m) => (
              <button
                key={m.slug}
                onClick={() => { setAutoCycle(false); nav(`/venture/${m.slug}`); }}
                className={
                  isHero
                    ? "rounded-full bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 hover:border-emerald-400 text-emerald-800 transition text-sm px-3 py-1.5"
                    : "rounded-full bg-white/10 hover:bg-emerald-500/25 border border-white/15 hover:border-emerald-400 text-white transition text-xs px-2.5 py-1"
                }
              >
                {m.name}
              </button>
            ))}
            <button
              onClick={() => nav(`/business-ideas?capital=${capital}`)}
              className={
                isHero
                  ? "rounded-full bg-slate-900 hover:bg-slate-800 text-white transition text-sm font-semibold px-3 py-1.5"
                  : "rounded-full bg-white/15 hover:bg-white/25 border border-white/25 text-white transition text-xs font-bold px-3 py-1"
              }
              data-testid="hero-slider-view-all"
            >
              See all {fit.total_fit} →
            </button>
          </div>
        )}
      </div>
      {fit && (
        <div className={`mt-3 ${isHero ? "text-xs text-slate-600" : "text-[11px] text-slate-400"}`}>
          <b className={isHero ? "text-emerald-700" : "text-emerald-300"}>{fit.total_fit}</b> matches for this budget
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// WhatsApp lead capture — appears inside the hero Instant Match Preview
// so visitors who aren't ready for full OTP still convert into warm leads.
// ---------------------------------------------------------------------------
function WhatsAppCapture({ capital, totalFit, matchedSlugs }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const isValidMobile = /^[6-9]\d{9}$/.test(mobile.trim().replace(/\D/g, "").slice(-10));

  const submit = async () => {
    if (!isValidMobile) return;
    setSubmitting(true);
    try {
      const payload = {
        mobile: `+91${mobile.trim().replace(/\D/g, "").slice(-10)}`,
        name: name.trim(),
        capital,
        matched_venture_slugs: matchedSlugs || [],
        total_fit: totalFit,
      };
      const { data } = await api.post("/leads/match-preview", payload);
      setDone(true);
      if (data.whatsapp_sent) {
        toast.success("Sent to your WhatsApp");
      } else {
        toast.success("Saved — we'll send it soon");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save right now");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="hero-whatsapp-success"
        className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-center"
      >
        <div className="inline-flex items-center gap-1.5 text-emerald-700 text-xs font-semibold uppercase tracking-widest">
          <Check className="h-3.5 w-3.5" strokeWidth={4} />
          Saved
        </div>
        <div className="mt-1 text-sm text-slate-800">
          {t("landing.matches_prefix", { count: totalFit })}<b>{t("landing.matches_channel")}</b>{t("landing.matches_suffix")}
        </div>
        <div className="mt-2 text-[11px] text-slate-500">
          {t("landing.prefer_report")}<b>{t("landing.prefer_cta")}</b>{t("landing.prefer_suffix")}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4" data-testid="hero-whatsapp-capture">
      {!open ? (
        <button
          data-testid="hero-whatsapp-toggle"
          onClick={() => setOpen(true)}
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 hover:bg-emerald-50 hover:border-emerald-400 py-2 text-xs font-medium text-slate-700 hover:text-emerald-800 transition"
        >
          <WhatsAppIcon />
          Save these matches to WhatsApp
        </button>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3.5" data-testid="hero-whatsapp-form">
          <div className="text-[11px] uppercase tracking-widest text-emerald-700 inline-flex items-center gap-1.5">
            <WhatsAppIcon /> WhatsApp preview
          </div>
          <div className="mt-2.5 space-y-2">
            <input
              data-testid="hero-whatsapp-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="w-full rounded-full bg-white border border-slate-200 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-emerald-500"
            />
            <div className="flex items-stretch gap-2">
              <div className="relative flex-1 min-w-0">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-mono">+91</span>
                <input
                  data-testid="hero-whatsapp-mobile"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/[^\d]/g, "").slice(0, 10))}
                  placeholder="98765 43210"
                  aria-label="10-digit mobile"
                  className="w-full rounded-full bg-white border border-slate-200 pl-11 pr-14 py-2.5 text-base sm:text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-emerald-500 font-mono tracking-wide"
                />
                {mobile.length > 0 && (
                  <span
                    data-testid="hero-whatsapp-mobile-counter"
                    className={`absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-mono ${
                      isValidMobile ? "text-emerald-600" : "text-slate-400"
                    }`}
                  >
                    {mobile.length}/10
                  </span>
                )}
              </div>
              <Button
                data-testid="hero-whatsapp-submit"
                onClick={submit}
                disabled={!isValidMobile || submitting}
                className="shrink-0 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold h-auto px-5 py-2.5 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "…" : "Send"}
              </Button>
            </div>
          </div>
          <div className="mt-2 text-[10px] text-slate-500 leading-relaxed">
            One message · No spam · <button onClick={() => setOpen(false)} className="underline hover:text-slate-800">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function WhatsAppIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M20.52 3.48A11.9 11.9 0 0 0 12 0a12 12 0 0 0-10.35 18L0 24l6.2-1.63A12 12 0 1 0 20.52 3.48ZM12 21.82a9.8 9.8 0 0 1-4.98-1.36l-.36-.21-3.68.97.98-3.59-.24-.37A9.83 9.83 0 1 1 12 21.82Zm5.4-7.35c-.3-.15-1.75-.87-2.02-.97s-.47-.15-.67.15-.77.97-.94 1.17-.35.22-.65.07a8 8 0 0 1-2.36-1.46 8.86 8.86 0 0 1-1.63-2.03c-.17-.3 0-.46.13-.61s.3-.35.45-.52a2.03 2.03 0 0 0 .3-.5.55.55 0 0 0 0-.52c-.07-.15-.67-1.62-.92-2.22s-.5-.51-.67-.52h-.57a1.1 1.1 0 0 0-.8.37 3.35 3.35 0 0 0-1.05 2.5 5.82 5.82 0 0 0 1.22 3.1c.15.2 2.11 3.23 5.12 4.53a17.11 17.11 0 0 0 1.7.63 4.1 4.1 0 0 0 1.88.12 3.08 3.08 0 0 0 2-1.42 2.53 2.53 0 0 0 .17-1.42c-.07-.12-.27-.2-.57-.35Z"/>
    </svg>
  );
}
